import { lazy, Suspense, useEffect, useRef } from "react";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import SEO, { organizationSchema } from "@/components/SEO";
// Lazy load ALL components below the fold for better TTI
const About = lazy(() => import("@/components/About"));
const Services = lazy(() => import("@/components/Services"));
const Stats = lazy(() => import("@/components/Stats"));
const Team = lazy(() => import("@/components/Team"));
const Clients = lazy(() => import("@/components/Clients"));
const Transactions = lazy(() => import("@/components/Transactions"));
const Contact = lazy(() => import("@/components/Contact"));

// Minimal loading fallback - only visible briefly
const SectionFallback = () => (
  <div className="min-h-[100px]" />
);

// Section IDs that will be tracked
const TRACKED_SECTIONS = ['sobre', 'servicos', 'stats', 'transacoes', 'equipe', 'clientes', 'contato'];

const Index = () => {
  const trackingInitialized = useRef(false);
  
  // Lazy load section tracking to defer Supabase SDK
  useEffect(() => {
    if (trackingInitialized.current) return;
    trackingInitialized.current = true;
    
    // Load tracking after page is interactive
    const initTracking = () => {
      import('@/hooks/useSectionTracking').then(({ initSectionTracking }) => {
        initSectionTracking(TRACKED_SECTIONS);
      });
    };
    
    const scheduleInit = () => {
      const requestIdleCallback =
        (window as Window & {
          requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
        }).requestIdleCallback;

      if (requestIdleCallback) {
        requestIdleCallback(initTracking, { timeout: 1500 });
      } else {
        setTimeout(initTracking, 200);
      }
    };

    if (document.readyState === 'complete') {
      scheduleInit();
    } else {
      window.addEventListener('load', scheduleInit, { once: true });
    }
  }, []);

  return (
    <main className="overflow-x-hidden">
      <SEO
        title="Cornerstone | Valuation, M&A e Consultoria"
        description="Boutique de M&A especializada em fusões, aquisições, valuation e consultoria empresarial. Assessoria estratégica para transações de alto valor."
        url="/"
        jsonLd={organizationSchema}
      />
      <Header />
      <Hero />

      <div className="content-visibility-auto">
        <Suspense fallback={<SectionFallback />}>
          <About />
        </Suspense>
      </div>

      <div className="content-visibility-auto">
        <Suspense fallback={<SectionFallback />}>
          <Services />
        </Suspense>
      </div>

      <div className="content-visibility-auto">
        <Suspense fallback={<SectionFallback />}>
          <Stats />
        </Suspense>
      </div>

      <div className="content-visibility-auto">
        <Suspense fallback={<SectionFallback />}>
          <Transactions />
        </Suspense>
      </div>

      <div className="content-visibility-auto">
        <Suspense fallback={<SectionFallback />}>
          <Team />
        </Suspense>
      </div>

      <div className="content-visibility-auto">
        <Suspense fallback={<SectionFallback />}>
          <Clients />
        </Suspense>
      </div>

      <div className="content-visibility-auto">
        <Suspense fallback={<SectionFallback />}>
          <Contact />
        </Suspense>
      </div>
    </main>
  );
};

export default Index;
